import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api';
import { StackedDaily, BarRows } from '../ui/charts';
import { GetStarted } from './Onboarding';
import { Ago, catLabel, Empty, Figure, Figures, fmtNum, PageHead, plural, Protection, Q, Section, Seg, Severity } from '../ui/kit';

const TREND = [
  { key: 'blocked', label: 'Blocked', color: 'var(--series-block)' },
  { key: 'sensitive_allowed', label: 'Sensitive, allowed', color: 'var(--series-allow)' },
  { key: 'warned', label: 'Warned / justified', color: 'var(--series-warn)' },
];

export interface BillableUsersSummary { directory: number; observed: number; unattributedDevices: number; billable: number; windowDays: number }

/**
 * Billing metric. Ward is priced per user per month: the billable count is the larger of the Entra directory
 * (enabled users) and the users seen on a live browser in the window, so an employee with two computers counts once.
 * Devices with no user are listed so the number is transparent, never silently added.
 */
export function BillableUsers({ b }: { b: BillableUsersSummary }) {
  const basis = b.directory >= b.observed ? 'directory' : 'observed';
  return (
    <Section title="Billable users" aside={<Link to="/discover/users">People</Link>}>
      <table className="t">
        <tbody>
          <tr>
            <td>Directory users<div className="sub">enabled users synchronised from Entra ID{b.directory === 0 ? ' (not connected)' : ''}</div></td>
            <td className="num">{fmtNum(b.directory)}{basis === 'directory' && <span className="sub"> · billed</span>}</td>
          </tr>
          <tr>
            <td>Users seen in the last {b.windowDays} days<div className="sub">distinct users with a live browser, however many computers each has</div></td>
            <td className="num">{fmtNum(b.observed)}{basis === 'observed' && <span className="sub"> · billed</span>}</td>
          </tr>
          <tr>
            <td>Unattributed devices<div className="sub">live browsers in the window with no user; not billed</div></td>
            <td className="num">{fmtNum(b.unattributedDevices)}</td>
          </tr>
        </tbody>
      </table>
      <p className="sub mt-8">Billed per user per month: the larger of your directory size and users seen in the last {b.windowDays} days.</p>
    </Section>
  );
}

function delta(now: number, prev: number): string {
  if (!prev && !now) return 'no activity';
  if (!prev) return 'none in prior period';
  const pct = Math.round(((now - prev) / prev) * 100);
  return `${pct === 0 ? 'same as' : `${pct > 0 ? '+' : ''}${pct}% vs`} prior period`;
}

export function Dashboard() {
  const [days, setDays] = useState<'1' | '7' | '30'>('7');
  const q = useApi<any>(`/dashboard?days=${days}`, { refetchInterval: 60_000 });
  return (
    <>
      <PageHead
        title="Dashboard"
        sub="Where sensitive data is going, what was stopped, and whether every browser is protected."
        actions={<Seg label="Time range" value={days} onChange={setDays} options={[['1', '24 hours'], ['7', '7 days'], ['30', '30 days']]} />}
      />
      <GetStarted />
      <Q q={q} rows={10}>
        {(d) => (
          <>
            <Figures>
              <Figure label="Protected devices" value={fmtNum(d.devices.protected)} href="/discover/devices" unit={`/ ${fmtNum(d.devices.total)}`} sub={`${fmtNum(d.devices.active_24h)} active in 24h`} />
              <Figure label="Sensitive actions" value={fmtNum(d.events.sensitive)} href="/investigate/activity" sub={delta(d.events.sensitive, d.prevEvents.sensitive)} />
              <Figure label="Actions blocked" value={fmtNum(d.events.blocked)} tone={d.events.blocked ? 'block' : undefined} href="/investigate/activity?outcome=blocked" sub={delta(d.events.blocked, d.prevEvents.blocked)} />
              <Figure label="Open alerts" value={fmtNum(d.alerts.open)} href="/investigate/alerts?status=open,investigating" sub={`${fmtNum(d.alerts.open_high)} high or critical`} />
              <Figure label="Applications discovered" value={fmtNum(d.apps.discovered)} href="/discover/apps" sub={`${fmtNum(d.apps.new_in_window)} new · ${fmtNum(d.apps.unreviewed)} unreviewed`} />
              <Figure label="AI applications" value={fmtNum(d.apps.ai)} href="/discover/apps?view=ai" sub={`${(d.aiUsage as any[]).reduce((s, r) => s + r.users, 0)} user-account pairs`} />
              {d.billableUsers && <Figure label="Billable users" value={fmtNum(d.billableUsers.billable)} href="/discover/users" sub={`${fmtNum(d.billableUsers.directory)} in directory · ${fmtNum(d.billableUsers.observed)} seen in ${d.billableUsers.windowDays}d`} />}
            </Figures>

            <div className="cols cols-3-2">
              <Section title="Sensitive data activity" aside={`${days === '1' ? 'last 24 hours' : `last ${days} days`}, by day`}>
                {d.events.total === 0 ? <Empty title="No security events in this period">{d.devices.total === 0 ? <>No browser is enrolled yet. <Link to="/admin/deployment">Deploy the extension</Link> to start seeing events.</> : <>Events appear once enrolled browsers encounter a <Link to="/protect/policies">policy</Link>.</>}</Empty> : <StackedDaily data={d.trend} series={TREND} />}
              </Section>
              <Section title="Sensitive data by destination" aside="events with classified data">
                {d.movement.length === 0 ? (
                  <Empty title="No sensitive data movement detected" />
                ) : (
                  <BarRows
                    series={[{ key: 'b', label: 'Blocked', color: 'var(--series-block)' }, { key: 'o', label: 'Not blocked', color: 'var(--series-allow)' }]}
                    rows={(d.movement as any[]).map((m) => ({ label: catLabel(m.category), values: [m.blocked, m.total - m.blocked] }))}
                  />
                )}
              </Section>
            </div>

            <div className="cols cols-2">
              <Section title="Open high-severity alerts" aside={<Link to="/investigate/alerts">All alerts</Link>}>
                {d.highAlerts.length === 0 ? (
                  <Empty title="Nothing urgent" />
                ) : (
                  <table className="t">
                    <tbody>
                      {(d.highAlerts as any[]).map((a) => (
                        <tr key={a.id}>
                          <td className="col-narrow"><Severity s={a.severity} /></td>
                          <td><Link to={`/investigate/alerts/${a.id}`}>{a.title}</Link><div className="sub">{a.user_name ?? 'Unknown user'} · {plural(a.event_count, 'event', undefined, String)}</div></td>
                          <td className="num sub"><Ago at={a.last_seen_at} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
              <Section title="Devices needing attention" aside={<Link to="/discover/devices">All devices</Link>}>
                {d.degraded.length === 0 ? (
                  <Empty title="Every enrolled browser is protected" />
                ) : (
                  <table className="t">
                    <tbody>
                      {(d.degraded as any[]).map((x) => (
                        <tr key={x.id}>
                          <td><Link to={`/discover/devices/${x.id}`}>{x.label}</Link><div className="sub">{x.user_name ?? 'Unassigned'} · {x.reason}</div></td>
                          <td><Protection s={x.protection_state} /></td>
                          <td className="num sub">heartbeat <Ago at={x.last_heartbeat_at} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
            </div>

            <div className="cols cols-2">
              {d.billableUsers && <BillableUsers b={d.billableUsers} />}
              <Section title="Applications most involved in policy events">
                {d.topApps.length === 0 ? (
                  <Empty title="No policy events yet" />
                ) : (
                  <table className="t">
                    <thead><tr><th>Application</th><th>Category</th><th className="num">Events</th><th className="num">Blocked</th><th className="num">Users</th></tr></thead>
                    <tbody>
                      {(d.topApps as any[]).map((a) => (
                        <tr key={a.id}><td><Link to={`/discover/apps/${a.id}`}>{a.name}</Link></td><td className="sub">{catLabel(a.category)}</td><td className="num">{a.events}</td><td className="num">{a.blocked}</td><td className="num">{a.users}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
              <Section title="Generative AI usage by account type" aside={<Link to="/discover/apps?view=ai">AI applications</Link>}>
                {d.aiUsage.length === 0 ? (
                  <Empty title="No AI usage observed" />
                ) : (
                  <BarRows
                    series={[{ key: 'u', label: 'Users', color: 'var(--series-allow)' }]}
                    rows={(['corporate', 'personal', 'unknown'] as const).map((t) => {
                      const r = (d.aiUsage as any[]).find((x) => x.account_type === t);
                      return { label: t === 'unknown' ? 'Unrecognised account' : `${t[0]!.toUpperCase()}${t.slice(1)} account`, values: [r?.users ?? 0] };
                    })}
                  />
                )}
              </Section>
            </div>
          </>
        )}
      </Q>
    </>
  );
}
